import { getChartImageExporter } from "./chartExportRegistry.js";

const STRIP_SELECTORS = [
    "button",
    "input",
    "textarea",
    "select",
    ".ac-actionSet",
    ".ac-pushButton",
    ".ac-pushButton-disabled",
    ".ac-input-container",
    ".ac-input",
    ".ms-Dropdown-container",
    ".ms-ComboBox-container",
    ".ms-BasePicker",
    ".ms-TagPicker",
    "[data-ac-action]",
    ".ac-copy-feedback",
    "#conversation-picker"
];

export const CLIPBOARD_TEXT_COLOR = "#000000";

export const OFFICE_CLIPBOARD_STYLES = [
    `body { font-family: Calibri, 'Segoe UI', sans-serif; font-size: 11pt; color: ${CLIPBOARD_TEXT_COLOR}; }`,
    "table { border-collapse: collapse; width: 100%; }",
    `th, td { border: 1px solid #8a8886; padding: 4pt 8pt; vertical-align: top; text-align: left; color: ${CLIPBOARD_TEXT_COLOR}; }`,
    "th { background: #f3f2f1; font-weight: 600; }",
    `p, li, span, div { color: ${CLIPBOARD_TEXT_COLOR}; }`,
    "p { margin: 0 0 8pt 0; }",
    "ul, ol { margin: 0 0 8pt 24pt; }",
    `h1, h2, h3, h4 { font-family: Calibri, 'Segoe UI', sans-serif; color: ${CLIPBOARD_TEXT_COLOR}; }`,
    "img { max-width: 100%; }",
    `a { color: ${CLIPBOARD_TEXT_COLOR}; }`,
    "strong, b { font-weight: 600; }",
    "em, i { font-style: italic; }",
    "code { font-family: Consolas, 'Courier New', monospace; }"
].join(" ");

export function getAdaptiveCardRoot(node) {
    if (!node) return null;
    if (node.classList?.contains("ac-adaptiveCard")) return node;
    return node.querySelector?.(".ac-adaptiveCard") || node;
}

function isEmptyLayout(node) {
    if (!node) return false;
    const hasMedia = node.querySelector?.("img, svg, table, canvas, .ac-chart");
    const text = (node.textContent || "").replace(/\s+/g, "");
    return !hasMedia && !text;
}

export function pruneEmptyLayout(root) {
    if (!root?.querySelectorAll) return root;
    let removed = true;
    while (removed) {
        removed = false;
        [...root.querySelectorAll(".ac-columnSet, .ac-container")].forEach((node) => {
            if (node !== root && isEmptyLayout(node)) {
                node.remove();
                removed = true;
            }
        });
    }
    return root;
}

export function stripInteractiveControls(root) {
    if (!root?.querySelectorAll) return root;
    root.querySelectorAll(".ac-input-container").forEach((node) => {
        const host = node.parentElement;
        (host && host !== root ? host : node).remove();
    });
    STRIP_SELECTORS.forEach((selector) => {
        root.querySelectorAll(selector).forEach((node) => node.remove());
    });
    [...root.querySelectorAll("*")].forEach((node) => {
        if (node === root) return;
        const hidden = node.hidden
            || node.style?.display === "none"
            || node.style?.visibility === "hidden";
        if (hidden) {
            node.remove();
        }
    });
    pruneEmptyLayout(root);
    return root;
}

function applyBlackTextColor(element) {
    if (!element || element.nodeType !== 1) return;
    if (element.tagName === "IMG") return;
    element.style.color = CLIPBOARD_TEXT_COLOR;
    element.style.webkitTextFillColor = CLIPBOARD_TEXT_COLOR;
    if (element.hasAttribute("color")) {
        element.setAttribute("color", CLIPBOARD_TEXT_COLOR);
    }
}

export function forceBlackTextColor(root) {
    if (!root) return root;
    applyBlackTextColor(root);
    root.querySelectorAll?.("*").forEach(applyBlackTextColor);
    return root;
}

function copyCellContents(source, target) {
    while (source.firstChild) {
        target.appendChild(source.firstChild);
    }
}

export function convertRoleTablesToHtmlTables(root) {
    if (!root?.querySelectorAll) return root;
    [...root.querySelectorAll('[role="table"]')].forEach((roleTable) => {
        const table = document.createElement("table");
        table.setAttribute("border", "1");
        table.setAttribute("cellpadding", "4");
        table.setAttribute("cellspacing", "0");
        table.style.borderCollapse = "collapse";
        table.style.width = "100%";

        const rows = [...roleTable.children].filter((child) => child.getAttribute?.("role") === "row");
        rows.forEach((row, rowIndex) => {
            const tr = document.createElement("tr");
            const cells = [...row.children].filter((child) => {
                const role = child.getAttribute?.("role");
                return role === "cell" || role === "columnheader";
            });
            cells.forEach((cell) => {
                const isHeader = cell.getAttribute("role") === "columnheader" || rowIndex === 0;
                const td = document.createElement(isHeader ? "th" : "td");
                copyCellContents(cell, td);
                tr.appendChild(td);
            });
            if (tr.children.length) {
                table.appendChild(tr);
            }
        });
        roleTable.replaceWith(table);
    });
    return root;
}

export function convertColumnSetsToTables(root) {
    if (!root?.querySelectorAll) return root;
    [...root.querySelectorAll(".ac-columnSet")].forEach((columnSet) => {
        const columns = [...columnSet.children].filter((child) => child.nodeType === 1);
        if (columns.length < 2) return;
        const table = document.createElement("table");
        table.setAttribute("border", "0");
        table.setAttribute("cellpadding", "4");
        table.setAttribute("cellspacing", "0");
        table.style.borderCollapse = "collapse";
        table.style.width = "100%";
        const tr = document.createElement("tr");
        columns.forEach((column) => {
            const td = document.createElement("td");
            td.style.verticalAlign = "top";
            copyCellContents(column, td);
            tr.appendChild(td);
        });
        table.appendChild(tr);
        columnSet.replaceWith(table);
    });
    return root;
}

const SVG_PAINT_PROPERTIES = [
    "display",
    "fill",
    "fill-opacity",
    "opacity",
    "stroke",
    "stroke-width"
];

const SVG_TEXT_PROPERTIES = [
    "font-family",
    "font-size",
    "font-weight",
    "text-anchor"
];

function numericAttribute(element, name) {
    const value = Number(element.getAttribute?.(name));
    return Number.isFinite(value) && value > 0 ? value : 0;
}

export function measureElement(element, fallbackWidth, fallbackHeight) {
    const rect = typeof element.getBoundingClientRect === "function"
        ? element.getBoundingClientRect()
        : { width: 0, height: 0 };
    const measuredWidth = Math.max(
        Number(element.clientWidth) || 0,
        Number(rect.width) || 0,
        numericAttribute(element, "width")
    );
    const measuredHeight = Math.max(
        Number(element.clientHeight) || 0,
        Number(rect.height) || 0,
        numericAttribute(element, "height")
    );
    return {
        width: measuredWidth > 0 ? measuredWidth : fallbackWidth,
        height: measuredHeight > 0 ? measuredHeight : fallbackHeight
    };
}

export function copyComputedSvgStyles(source, target) {
    if (!source || !target || typeof getComputedStyle !== "function") return target;
    const computed = getComputedStyle(source);
    const tag = String(source.tagName || "").toLowerCase();
    const properties = tag === "text" || tag === "tspan"
        ? SVG_PAINT_PROPERTIES.concat(SVG_TEXT_PROPERTIES)
        : SVG_PAINT_PROPERTIES;
    properties.forEach((property) => {
        const value = computed.getPropertyValue(property)?.trim();
        if (value) {
            target.setAttribute(property, value);
        }
    });
    return target;
}

export function inlineSvgComputedStyles(liveSvg) {
    const clone = liveSvg.cloneNode(true);
    if (!clone.getAttribute("xmlns")) {
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    }
    const liveNodes = [liveSvg, ...liveSvg.querySelectorAll("*")];
    const cloneNodes = [clone, ...clone.querySelectorAll("*")];
    liveNodes.forEach((live, index) => {
        copyComputedSvgStyles(live, cloneNodes[index]);
    });
    const { width, height } = measureElement(liveSvg, 400, 220);
    clone.setAttribute("width", String(width));
    clone.setAttribute("height", String(height));
    if (!clone.getAttribute("viewBox")) {
        clone.setAttribute("viewBox", `0 0 ${width} ${height}`);
    }
    return { clone, width, height };
}

function isTransparentColor(value) {
    return !value
        || value === "transparent"
        || value === "rgba(0, 0, 0, 0)"
        || value === "rgba(0,0,0,0)";
}

export function collectHtmlLegendItems(chart, liveSvg) {
    if (!chart?.querySelectorAll) return [];
    return [...chart.querySelectorAll('[role="option"]')]
        .filter((option) => !liveSvg || !liveSvg.contains(option))
        .map((option) => {
            const nodes = [option, ...option.querySelectorAll("*")];
            let color = "";
            nodes.some((node) => {
                const background = typeof getComputedStyle === "function"
                    ? getComputedStyle(node).backgroundColor
                    : "";
                if (!isTransparentColor(background)) {
                    color = background;
                    return true;
                }
                return false;
            });
            const title = (option.textContent || "").replace(/\s+/g, " ").trim();
            return title ? { title, color: color || "#8a8886" } : undefined;
        })
        .filter(Boolean);
}

export function appendHtmlLegendToSvg(cloneSvg, legendItems, plotWidth, plotHeight) {
    if (!legendItems.length) {
        return { width: plotWidth, height: plotHeight };
    }
    const rowHeight = 22;
    const swatch = 12;
    const legendHeight = 12 + legendItems.length * rowHeight;
    cloneSvg.setAttribute("height", String(plotHeight + legendHeight));
    cloneSvg.setAttribute("viewBox", `0 0 ${plotWidth} ${plotHeight + legendHeight}`);
    legendItems.forEach((item, index) => {
        const y = plotHeight + 10 + index * rowHeight;
        const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        rect.setAttribute("x", "8");
        rect.setAttribute("y", String(y));
        rect.setAttribute("width", String(swatch));
        rect.setAttribute("height", String(swatch));
        rect.setAttribute("fill", item.color);
        const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
        text.setAttribute("x", "26");
        text.setAttribute("y", String(y + 10));
        text.setAttribute("fill", "#000000");
        text.setAttribute("font-family", "Segoe UI, Calibri, sans-serif");
        text.setAttribute("font-size", "12");
        text.textContent = item.title;
        cloneSvg.appendChild(rect);
        cloneSvg.appendChild(text);
    });
    return { width: plotWidth, height: plotHeight + legendHeight };
}

export function isChartTextNode(element) {
    const tag = String(element?.tagName || "").toLowerCase();
    return tag === "text" || tag === "tspan";
}

export function forceChartTextBlack(root) {
    if (!root?.querySelectorAll) return root;
    root.querySelectorAll("text, tspan").forEach((element) => {
        element.setAttribute("fill", CLIPBOARD_TEXT_COLOR);
        element.style.fill = CLIPBOARD_TEXT_COLOR;
        element.style.color = CLIPBOARD_TEXT_COLOR;
        element.style.webkitTextFillColor = CLIPBOARD_TEXT_COLOR;
        element.setAttribute("stroke", "none");
        element.style.stroke = "none";
    });
    root.querySelectorAll("[role='option'], [role='option'] *").forEach((element) => {
        if (isChartTextNode(element)) return;
        element.style.color = CLIPBOARD_TEXT_COLOR;
        element.style.webkitTextFillColor = CLIPBOARD_TEXT_COLOR;
    });
    return root;
}

export function applyTemporaryBlackChartText(chart) {
    if (!chart?.querySelectorAll) return () => {};
    const snapshots = [];
    const snapshot = (element) => {
        snapshots.push({
            element,
            fill: element.getAttribute("fill"),
            stroke: element.getAttribute("stroke"),
            styleFill: element.style.fill,
            styleStroke: element.style.stroke,
            styleColor: element.style.color,
            styleWebkit: element.style.webkitTextFillColor
        });
    };
    chart.querySelectorAll("text, tspan").forEach(snapshot);
    chart.querySelectorAll("[role='option'], [role='option'] *").forEach((element) => {
        if (!isChartTextNode(element)) snapshot(element);
    });
    forceChartTextBlack(chart);
    return () => {
        snapshots.forEach((item) => {
            if (item.fill == null) item.element.removeAttribute("fill");
            else item.element.setAttribute("fill", item.fill);
            if (item.stroke == null) item.element.removeAttribute("stroke");
            else item.element.setAttribute("stroke", item.stroke);
            item.element.style.fill = item.styleFill;
            item.element.style.stroke = item.styleStroke;
            item.element.style.color = item.styleColor;
            item.element.style.webkitTextFillColor = item.styleWebkit;
        });
    };
}

export async function withBlackChartText(chart, work) {
    const restore = applyTemporaryBlackChartText(chart);
    try {
        return await work();
    } finally {
        restore();
    }
}

export function prepareLiveChartSvg(svg) {
    const prepared = inlineSvgComputedStyles(svg);
    const host = svg.closest?.(".ac-chart");
    const legendItems = host ? collectHtmlLegendItems(host, svg) : [];
    const size = appendHtmlLegendToSvg(prepared.clone, legendItems, prepared.width, prepared.height);
    forceChartTextBlack(prepared.clone);
    return { clone: prepared.clone, width: size.width, height: size.height };
}

export function serializeSvg(svg) {
    const node = svg.cloneNode(true);
    if (!node.getAttribute("xmlns")) {
        node.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    }
    return new XMLSerializer().serializeToString(node);
}

export function createDrawingCanvas(width, height) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

export function defaultCreateImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Failed to load image for clipboard"));
        image.src = url;
    });
}

export function resolveRasterScale(scale) {
    if (Number(scale) > 0) return Number(scale);
    const dpr = typeof window !== "undefined" ? Number(window.devicePixelRatio) : 1;
    return dpr > 0 ? dpr : 1;
}

export async function drawUrlToPng(
    url,
    width,
    height,
    createImage = defaultCreateImage,
    createCanvas = createDrawingCanvas,
    scale = 1
) {
    const pixelWidth = Math.max(1, Math.round(width * scale));
    const pixelHeight = Math.max(1, Math.round(height * scale));
    const image = await createImage(url);
    const canvas = createCanvas(pixelWidth, pixelHeight);
    const context = canvas.getContext("2d");
    if (!context) {
        throw new Error("Canvas 2D context is unavailable");
    }
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, pixelWidth, pixelHeight);
    context.drawImage(image, 0, 0, pixelWidth, pixelHeight);
    return canvas.toDataURL("image/png");
}

export async function rasterizeSvgToPng(
    svg,
    createImage = defaultCreateImage,
    createCanvas = createDrawingCanvas,
    options = {}
) {
    const prepared = prepareLiveChartSvg(svg);
    const xml = serializeSvg(prepared.clone);
    const svgUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
    try {
        return await drawUrlToPng(
            svgUrl,
            prepared.width,
            prepared.height,
            createImage,
            createCanvas,
            resolveRasterScale(options.scale)
        );
    } catch (error) {
        return svgUrl;
    }
}

export function chartImageFromDataUrl(chart, dataUrl) {
    const img = document.createElement("img");
    img.src = dataUrl;
    img.alt = chart.getAttribute("aria-label") || "Chart";
    img.style.maxWidth = "100%";
    return img;
}

export async function rasterizeLiveChart(chart, options = {}) {
    const scale = resolveRasterScale(options.scale);
    const exportOpts = { background: "#ffffff", scale };
    if (typeof options.rasterizeChart === "function") {
        return options.rasterizeChart(chart, exportOpts);
    }
    const exporter = getChartImageExporter(chart);
    if (typeof exporter === "function") {
        try {
            return await withBlackChartText(chart, () => exporter(exportOpts));
        } catch (error) {
            // Fall back to an inlined SVG snapshot of the live chart.
        }
    }
    const rasterizeSvg = options.rasterizeSvg || rasterizeSvgToPng;
    const svg = chart.querySelector("svg");
    if (svg) {
        return rasterizeSvg(svg, options.createImage, options.createCanvas, { scale });
    }
    const canvas = chart.querySelector("canvas");
    if (canvas?.toDataURL) {
        return canvas.toDataURL("image/png");
    }
    return undefined;
}

export async function rasterizeLiveChartElements(root, options = {}) {
    if (!root?.querySelectorAll) return [];
    const urls = [];
    for (const chart of root.querySelectorAll(".ac-chart")) {
        try {
            urls.push(await rasterizeLiveChart(chart, options));
        } catch (error) {
            urls.push(undefined);
        }
    }
    return urls;
}

export function applyChartImages(root, dataUrls = []) {
    if (!root?.querySelectorAll) return root;
    [...root.querySelectorAll(".ac-chart")].forEach((chart, index) => {
        const dataUrl = dataUrls[index];
        if (!dataUrl) return;
        chart.replaceWith(chartImageFromDataUrl(chart, dataUrl));
    });
    return root;
}

export async function replaceChartsWithImages(root, rasterizeSvg = rasterizeSvgToPng) {
    if (!root?.querySelectorAll) return root;
    const dataUrls = await rasterizeLiveChartElements(root, { rasterizeSvg });
    return applyChartImages(root, dataUrls);
}

export function wrapOfficeHtml(innerHtml) {
    return [
        "<html xmlns:o=\"urn:schemas-microsoft-com:office:office\"",
        " xmlns:w=\"urn:schemas-microsoft-com:office:word\"",
        " xmlns=\"http://www.w3.org/TR/REC-html40\">",
        "<head><meta charset=\"utf-8\"><style>",
        OFFICE_CLIPBOARD_STYLES,
        "</style></head><body>",
        "<!--StartFragment-->",
        innerHtml,
        "<!--EndFragment-->",
        "</body></html>"
    ].join("");
}

export function htmlToPlainText(html) {
    const holder = document.createElement("div");
    holder.innerHTML = html;
    return (holder.innerText || holder.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

export async function writeRichClipboard(html, plainText, clipboard = navigator.clipboard) {
    if (typeof ClipboardItem === "function" && clipboard?.write) {
        const item = new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plainText], { type: "text/plain" })
        });
        await clipboard.write([item]);
        return "clipboard-item";
    }
    if (clipboard?.writeText) {
        await clipboard.writeText(plainText);
        return "text";
    }
    return copyViaExecCommand(html, plainText);
}

export function copyViaExecCommand(html, plainText) {
    const holder = document.createElement("div");
    holder.contentEditable = "true";
    holder.style.position = "fixed";
    holder.style.left = "-9999px";
    holder.innerHTML = html;
    document.body.appendChild(holder);
    const selection = window.getSelection?.();
    const range = document.createRange?.();
    let copied = false;
    try {
        if (selection && range) {
            range.selectNodeContents(holder);
            selection.removeAllRanges();
            selection.addRange(range);
        }
        copied = document.execCommand("copy");
    } catch (error) {
        copied = false;
    }
    selection?.removeAllRanges?.();
    holder.remove();
    if (!copied) {
        throw new Error("Clipboard copy is not available");
    }
    return "execCommand";
}

export function showCopiedFeedback(button, message = "Copied to clipboard") {
    if (!button?.insertAdjacentElement) return undefined;
    const parent = button.parentElement;
    parent?.querySelectorAll?.(".ac-copy-feedback").forEach((node) => node.remove());
    const toast = document.createElement("span");
    toast.className = "ac-copy-feedback";
    toast.setAttribute("role", "status");
    toast.textContent = message;
    button.insertAdjacentElement("beforeend", toast);
    const previousTitle = button.title;
    button.title = message;
    const timeoutId = setTimeout(() => {
        toast.remove();
        if (button.title === message) {
            button.title = previousTitle;
        }
    }, 1600);
    return { toast, timeoutId, previousTitle };
}

export async function buildClipboardHtml(cardRoot, options = {}) {
    const source = getAdaptiveCardRoot(cardRoot);
    if (!source) return "";
    const chartImages = await rasterizeLiveChartElements(source, options);
    const clone = source.cloneNode(true);
    applyChartImages(clone, chartImages);
    stripInteractiveControls(clone);
    convertRoleTablesToHtmlTables(clone);
    convertColumnSetsToTables(clone);
    forceBlackTextColor(clone);
    return clone.innerHTML;
}

export async function copyRenderedCardToClipboard(cardRoot, options = {}) {
    const innerHtml = await buildClipboardHtml(cardRoot, options);
    const html = wrapOfficeHtml(innerHtml);
    const plainText = htmlToPlainText(innerHtml);
    const method = await writeRichClipboard(html, plainText, options.clipboard);
    showCopiedFeedback(options.triggerButton, options.feedbackMessage);
    return { html, plainText, method };
}
